import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import React from 'react';
import styled, { css } from 'styled-components';
import { theme } from "../../theme";
import { baseTextStyles, textStyles } from "../Text/Text";
import { ghostVariant } from "./ghost";
import { linkVariant } from "./link";
import { containedVariant } from "./contained";
import { IconBefore, IconAfter } from "./Icons";
import { useSharedHoverContext } from "../SharedHover/SharedHoverContext";
import { mergeProps } from '@react-aria/utils';
import { focusStyles, hoverTransition } from "../Interactive/mixins";
/**
 * Sizes
 */

export var sizeToIconSize = {
  small: '16',
  medium: '24'
};
export var buttonSizes = {
  small: 24,
  medium: 32
};
export var defaultButtonPadding = "0 ".concat(theme.spaces[12]);
var sizes = {
  small: css(["height:", "px;line-height:", "px;padding:", ";"], buttonSizes.small, buttonSizes.small, defaultButtonPadding),
  medium: css(["height:", "px;line-height:", "px;padding:", ";"], buttonSizes.medium, buttonSizes.medium, defaultButtonPadding)
};
/**
 * Button variants
 */

export var buttonVariants = {
  link: linkVariant(),
  contained: containedVariant(),
  ghost: ghostVariant()
};
export var Button = React.forwardRef(function (_ref, ref) {
  var _iconBefore$props$siz, _iconAfter$props$size;

  var children = _ref.children,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? 'medium' : _ref$size,
      _ref$color = _ref.color,
      color = _ref$color === void 0 ? 'secondary' : _ref$color,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      _ref$variant = _ref.variant,
      variant = _ref$variant === void 0 ? 'contained' : _ref$variant,
      iconBefore = _ref.iconBefore,
      iconAfter = _ref.iconAfter,
      _ref$block = _ref.block,
      block = _ref$block === void 0 ? false : _ref$block,
      as = _ref.as,
      other = _objectWithoutProperties(_ref, ["children", "size", "color", "disabled", "variant", "iconBefore", "iconAfter", "block", "as"]);

  var isLink = other.href;

  var _useSharedHoverContex = useSharedHoverContext(),
      onEnterTarget = _useSharedHoverContex.onEnterTarget,
      onLeaveTarget = _useSharedHoverContex.onLeaveTarget,
      enabled = _useSharedHoverContex.enabled;

  function onMouseEnter(e) {
    onEnterTarget(e.currentTarget.getBoundingClientRect());
  }

  function onMouseLeave() {
    onLeaveTarget();
  }

  return /*#__PURE__*/React.createElement(StyledButton, _extends({
    disabled: disabled,
    $color: color,
    $size: size,
    $variant: variant,
    $sharedHoverEnabled: enabled && variant === 'ghost',
    $block: block,
    ref: ref,
    as: isLink ? 'a' : as
  }, mergeProps(other, variant === 'ghost' && {
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave
  })), iconBefore && React.isValidElement(iconBefore) && /*#__PURE__*/React.createElement(IconBefore, null, React.cloneElement(iconBefore, {
    'aria-hidden': true,
    size: (_iconBefore$props$siz = iconBefore.props.size) !== null && _iconBefore$props$siz !== void 0 ? _iconBefore$props$siz : sizeToIconSize[size]
  })), children, iconAfter && React.isValidElement(iconAfter) && /*#__PURE__*/React.createElement(IconAfter, null, React.cloneElement(iconAfter, {
    'aria-hidden': true,
    size: (_iconAfter$props$size = iconAfter.props.size) !== null && _iconAfter$props$size !== void 0 ? _iconAfter$props$size : sizeToIconSize[size]
  })));
});
export var buttonReset = css(["text-decoration:none;background:none;white-space:nowrap;box-sizing:border-box;text-align:center;border:none;user-select:none;-webkit-tap-highlight-color:transparent;cursor:pointer;box-shadow:none;padding:0;"]);
export var StyledButton = styled.button.withConfig({
  displayName: "Button__StyledButton",
  componentId: "vapor__sc-14ws2gb-0"
})(["", ";", ";", ";outline:none;align-items:center;position:relative;border-radius:", ";display:inline-flex;vertical-align:middle;justify-content:center;", ";", ";", ";", ";", ""], buttonReset, baseTextStyles, textStyles.link, theme.radii[6], hoverTransition, function (p) {
  return sizes[p.$size];
}, function (p) {
  return buttonVariants[p.$variant];
}, focusStyles, function (p) {
  return p.$block && "\n    width: 100%;\n  ";
});